/**
 * Padel USA — smooth mouse drag for product carousels (.resource-list__carousel).
 *
 * Replaces Horizon's mouse drag (slideshow.js #handleMouseDown) on product rows only. The stock
 * drag felt laggy and "snappy-back" on desktop because:
 *   - release velocity was the last move divided by the WHOLE drag time, so a flick read as ~0
 *     and the row snapped back to the card nearest the left edge instead of moving on;
 *   - a release moved at most one card, with no momentum;
 *   - [dragging] turned off pointer-events on every card (hover zoom dropped mid-drag) and hid the
 *     arrows, which then re-animated in on release.
 * Here the row follows the mouse 1:1 (one scroll write per frame), carries the release velocity
 * into a decelerating glide, and lands on the nearest card edge. Touch is untouched: phones and
 * tablets keep native swipe scrolling (mousedown is mouse-only).
 *
 * Runs in the capture phase and stops the mousedown there, so the stock handler on the scroller
 * never starts. The slideshow still tracks the current slide/arrows through its scroll listener.
 * Dossier: custom-code-padelusa/features/carousel-drag-and-arrows.md
 */

const CAROUSEL = '.resource-list__carousel';
const IGNORE = 'input, select, textarea, [contenteditable], model-viewer, slideshow-arrows, slideshow-controls';
const DRAG_THRESHOLD = 5; // px of movement before a press becomes a drag (below = a click)
const VELOCITY_WINDOW = 100; // ms of pointer history used for the release velocity
const FLICK_VELOCITY = 0.25; // px/ms — above this, release always advances in the drag direction
const GLIDE = 280; // ms — how far the release velocity carries the row before snapping

/** @type {WeakMap<HTMLElement, number>} running glide animation per scroller */
const glides = new WeakMap();

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The row's own scroller. Product cards nest a photo-gallery slideshow, so the first
 * slideshow-slides inside the carousel (document order = outermost) is the row.
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function rowScroller(target) {
  if (!(target instanceof Element)) return null;
  const scroller = target.closest(CAROUSEL)?.querySelector('slideshow-slides');
  return scroller instanceof HTMLElement && scroller.contains(target) ? scroller : null;
}

/** @param {HTMLElement} scroller */
function stopGlide(scroller) {
  const id = glides.get(scroller);
  if (id != null) cancelAnimationFrame(id);
  glides.delete(scroller);
}

/** @param {HTMLElement} scroller */
function setFree(scroller, free) {
  // Snap and smooth-scroll fight a hand-driven position; restore them once the row has landed.
  // A class (non-inherited properties only, see base.css) restyles just the scroller — an inline
  // style or an inherited property (cursor) restyled all ~600 card nodes at drag start and end.
  scroller.classList.toggle('is-mouse-dragging', free);
}

/**
 * Scroll positions where a card's start edge lines up with the track start (same maths as the
 * theme's Scroller.to), clamped to the scrollable range and de-duplicated.
 * @param {HTMLElement} scroller
 */
function snapPoints(scroller) {
  const max = scroller.scrollWidth - scroller.clientWidth;
  const padStart = parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
  const points = new Set([0, max]);
  for (const slide of scroller.children) {
    if (!(slide instanceof HTMLElement) || slide.hidden) continue;
    points.add(Math.min(max, Math.max(0, slide.offsetLeft - padStart)));
  }
  return [...points].sort((a, b) => a - b);
}

/**
 * @param {HTMLElement} scroller
 * @param {number} velocity scroll velocity in px/ms (positive = towards the end)
 */
function landingPoint(scroller, velocity) {
  const points = snapPoints(scroller);
  const from = scroller.scrollLeft;
  const projected = from + velocity * GLIDE;
  let target = points.reduce((best, p) => (Math.abs(p - projected) < Math.abs(best - projected) ? p : best), points[0]);

  // A deliberate flick never lands back where it came from.
  if (Math.abs(velocity) > FLICK_VELOCITY) {
    if (velocity > 0 && target <= from) target = points.find((p) => p > from + 1) ?? target;
    if (velocity < 0 && target >= from) target = [...points].reverse().find((p) => p < from - 1) ?? target;
  }
  return target;
}

/**
 * Ease to the target, starting at the release speed so the hand-off from drag to glide is seamless.
 * @param {HTMLElement} scroller
 * @param {number} target
 * @param {number} velocity px/ms
 */
function glideTo(scroller, target, velocity) {
  stopGlide(scroller);
  const from = scroller.scrollLeft;
  const distance = target - from;

  if (Math.abs(distance) < 1 || reducedMotion()) {
    scroller.scrollLeft = target;
    setFree(scroller, false);
    return;
  }

  // easeOutCubic starts at 3 × average speed: pick the duration that matches the release speed.
  const speed = Math.abs(velocity);
  const matched = speed > 0.05 && Math.sign(velocity) === Math.sign(distance) ? (3 * Math.abs(distance)) / speed : 0;
  const duration = Math.min(700, Math.max(260, matched || 220 + Math.abs(distance) * 0.6));
  const start = performance.now();

  const step = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    scroller.scrollLeft = from + distance * eased;
    if (t < 1) {
      glides.set(scroller, requestAnimationFrame(step));
    } else {
      glides.delete(scroller);
      setFree(scroller, false);
    }
  };
  glides.set(scroller, requestAnimationFrame(step));
}

/** @param {MouseEvent} event */
function onMouseDown(event) {
  if (event.button !== 0 || !(event.target instanceof Element)) return;
  const scroller = rowScroller(event.target);
  if (!scroller) return;

  // Always own product-row presses so the stock drag never starts; plain clicks still go through.
  event.stopPropagation();
  if (event.target.closest(IGNORE)) return; // form controls keep their normal press
  if (scroller.closest('slideshow-component')?.getAttribute('disabled') === 'true') return; // too few cards
  if (scroller.scrollWidth <= scroller.clientWidth + 1) return;

  event.preventDefault(); // no native image/link drag, no text selection
  const wasGliding = glides.has(scroller);
  stopGlide(scroller);

  const startX = event.clientX;
  const startScroll = scroller.scrollLeft;
  /** @type {Array<[number, number]>} [time, clientX] */
  let samples = [[event.timeStamp, startX]];
  let moved = false;
  let pendingScroll = null;
  let frame = 0;

  const flush = () => {
    frame = 0;
    if (pendingScroll != null) scroller.scrollLeft = pendingScroll;
  };

  /** @param {MouseEvent} e */
  const onMove = (e) => {
    const dx = e.clientX - startX;
    if (!moved) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      moved = true;
      setFree(scroller, true);
    }
    samples.push([e.timeStamp, e.clientX]);
    const cutoff = e.timeStamp - VELOCITY_WINDOW;
    while (samples.length > 2 && samples[0][0] < cutoff) samples.shift();
    pendingScroll = startScroll - dx;
    if (!frame) frame = requestAnimationFrame(flush);
  };

  /** @param {MouseEvent} e */
  const onUp = (e) => {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    window.removeEventListener('blur', onUp);
    if (frame) cancelAnimationFrame(frame);
    flush();
    if (!moved) {
      // A press that only stopped a glide: settle on the nearest card, and don't open it.
      if (wasGliding) {
        suppressNextClick();
        glideTo(scroller, landingPoint(scroller, 0), 0);
      }
      return;
    }

    suppressNextClick();

    // Release velocity from the last ~100ms only. A pause before letting go means no flick.
    const now = e.timeStamp || performance.now();
    const [t0, x0] = samples[0];
    const [t1, x1] = samples[samples.length - 1];
    const fresh = now - t1 < 60 && t1 > t0;
    const velocity = fresh ? -(x1 - x0) / (t1 - t0) : 0; // pointer left → row moves towards the end

    glideTo(scroller, landingPoint(scroller, velocity), velocity);
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
  window.addEventListener('blur', onUp);
}

// A drag must not open the card the mouse was released over.
function suppressNextClick() {
  const swallow = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  window.addEventListener('click', swallow, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', swallow, { capture: true }), 0);
}

// Wheel / trackpad or touch on a gliding row hands control straight back to the user.
function interrupt(event) {
  const scroller = rowScroller(event.target);
  if (scroller && glides.has(scroller)) {
    stopGlide(scroller);
    setFree(scroller, false);
  }
}

if (!window.__pusaCarouselDrag) {
  window.__pusaCarouselDrag = true;
  document.addEventListener('mousedown', onMouseDown, { capture: true });
  document.addEventListener('wheel', interrupt, { capture: true, passive: true });
  document.addEventListener('touchstart', interrupt, { capture: true, passive: true });
}
